import React, { useEffect, useState } from "react";
import api, { SHARE } from "@/lib/api";
import { waLink } from "@/lib/wa";
import { toast } from "sonner";
import { Search, Trash2, FileText, MessageCircle, MailCheck, MailX } from "lucide-react";
import { Modal, Field, inputCls, Btn, ConfirmDialog } from "./kit";

const STATUSES = ["New", "In Progress", "Quoted", "Negotiation", "Won", "Lost", "Archived"];
const STATUS_COLOR = {
  New: "bg-blue-500/15 text-blue-300", "In Progress": "bg-amber-500/15 text-amber-300",
  Quoted: "bg-purple-500/15 text-purple-300", Negotiation: "bg-cyan-500/15 text-cyan-300",
  Won: "bg-emerald-500/15 text-emerald-300", Lost: "bg-rose-500/15 text-rose-300",
  Archived: "bg-offwhite/10 text-offwhite/50",
};

// Turn a buyer phone into WhatsApp international format (digits only).
// Local Indonesian numbers (0812...) become 62812...; others are used as typed.
const toWaNumber = (phone, country = "") => {
  const raw = (phone || "").trim();
  let d = raw.replace(/[^0-9]/g, "");
  if (!d) return "";
  if (raw.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0") && /indonesia/i.test(country)) d = `62${d.slice(1)}`;
  return d.length >= 8 ? d : "";
};

const priceSheetMessage = (r) => {
  const name = (r.full_name || "").split(" ")[0] || "there";
  return `Hi ${name}, thank you for your inquiry ${r.rfq_number} with Warehouse Coffee. Here is our latest green coffee stock & price sheet: ${SHARE.priceSheet}\n\nPrices are quoted on request - just reply here with your target quantity, packaging and destination port.`;
};

const CONFIRM_LABEL = {
  sent: ["Confirmation email sent", "text-emerald-300 bg-emerald-500/10", MailCheck],
  skipped_rate_limited: ["Confirmation skipped (limit reached)", "text-amber-300 bg-amber-500/10", MailX],
  skipped_invalid_email: ["Confirmation skipped (invalid email)", "text-amber-300 bg-amber-500/10", MailX],
  failed: ["Confirmation email failed", "text-rose-300 bg-rose-500/10", MailX],
};

export default function AdminRFQ() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [active, setActive] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => { try { const r = await api.get("/admin/rfq"); setItems(r.data); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try { const r = await api.put(`/admin/rfq/${id}`, patch); setItems((its) => its.map((x) => x.id === id ? r.data : x)); if (active?.id === id) setActive(r.data); toast.success("Updated"); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async () => { try { await api.delete(`/admin/rfq/${confirm}`); toast.success("Deleted"); setConfirm(null); setActive(null); load(); } catch (e) { toast.error("Failed"); } };

  const filtered = items.filter((r) => {
    const mq = !q || [r.rfq_number, r.company, r.full_name, r.product, r.country].join(" ").toLowerCase().includes(q.toLowerCase());
    const mf = filter === "ALL" || r.status === filter;
    return mq && mf;
  });

  return (
    <div>
      <div className="mb-6"><h1 className="font-display text-2xl font-extrabold text-offwhite">Requests for Quotation</h1><p className="mt-1 font-body text-sm text-offwhite/55">Manage buyer inquiries.</p></div>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-offwhite/40" />
          <input data-testid="rfq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search RFQs…" className={`${inputCls} pl-10`} />
        </div>
        <div className="flex flex-wrap gap-2">
          {["ALL", ...STATUSES].map((s) => <button key={s} onClick={() => setFilter(s)} data-testid={`rfq-filter-${s.toLowerCase().replace(/ /g,'-')}`} className={`rounded-full border px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider ${filter === s ? "border-amber-brand bg-amber-brand/20 text-amber-soft" : "border-offwhite/15 text-offwhite/60"}`}>{s}</button>)}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-offwhite/10 bg-[#101c14]">
        <table className="w-full min-w-[820px] text-left">
          <thead className="border-b border-offwhite/10"><tr className="font-mono text-[10px] uppercase tracking-wider text-offwhite/50">{["RFQ #", "Date", "Company", "Product", "Qty", "Country", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={8} className="px-4 py-12 text-center font-body text-sm text-offwhite/40">No RFQs yet.</td></tr> : filtered.map((r) => (
              <tr key={r.id} data-testid={`rfq-row-${r.id}`} className="cursor-pointer border-b border-offwhite/5 font-body text-sm hover:bg-offwhite/[0.03]" onClick={() => setActive(r)}>
                <td className="px-4 py-3 font-mono text-xs text-amber-soft">{r.rfq_number}</td>
                <td className="px-4 py-3 font-mono text-[10px] text-offwhite/50">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-offwhite/85">{r.company}</td>
                <td className="px-4 py-3 text-offwhite/70">{r.product}</td>
                <td className="px-4 py-3 font-mono text-xs text-offwhite/70">{r.quantity} {r.unit}</td>
                <td className="px-4 py-3 text-offwhite/70">{r.country}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase ${STATUS_COLOR[r.status] || ""}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-right"><span className="font-mono text-[11px] text-offwhite/50">View →</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!active} onClose={() => setActive(null)} title={active?.rfq_number} wide>
        {active && (
          <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {[["Name", active.full_name], ["Company", active.company], ["Country", active.country], ["Email", active.email], ["Phone", active.phone], ["Product", active.product], ["Origin", active.origin], ["Process", active.process], ["Quantity", `${active.quantity} ${active.unit}`], ["Packaging", active.packaging], ["Delivery", active.delivery_date], ["Destination", active.destination_port]].filter(([,v]) => v).map(([k, v]) => (
                <div key={k}><div className="font-mono text-[10px] uppercase tracking-wider text-offwhite/45">{k}</div><div className="mt-0.5 font-body text-sm text-offwhite/85">{v}</div></div>
              ))}
            </div>
            <div className="flex flex-col gap-3 rounded-lg border border-[#25D366]/25 bg-[#25D366]/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="rfq-whatsapp-panel">
              <div>
                <div className="font-display text-sm font-bold text-offwhite">Send price sheet on WhatsApp</div>
                <p className="mt-0.5 font-body text-xs text-offwhite/55">
                  {toWaNumber(active.phone, active.country)
                    ? `Opens WhatsApp to +${toWaNumber(active.phone, active.country)} with the latest price sheet link.`
                    : "No usable phone number on this request - pick the contact in WhatsApp instead."}
                </p>
              </div>
              <a href={waLink(toWaNumber(active.phone, active.country), priceSheetMessage(active))} target="_blank" rel="noreferrer" data-testid="rfq-send-price-sheet-whatsapp" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#0b2a17] transition-colors hover:bg-[#3ee07a]">
                <MessageCircle className="h-4 w-4" /> {toWaNumber(active.phone, active.country) ? "Send price sheet" : "Choose contact"}
              </a>
            </div>
            {CONFIRM_LABEL[active.confirmation_email] && (() => {
              const [label, cls, Icon] = CONFIRM_LABEL[active.confirmation_email];
              return <div data-testid="rfq-confirmation-status" className={`inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-[10px] uppercase ${cls}`}><Icon className="h-3.5 w-3.5" /> {label}</div>;
            })()}
            <div className="flex flex-wrap gap-2">
              {active.request_sample && <span className="rounded-full bg-amber-brand/15 px-3 py-1 font-mono text-[10px] uppercase text-amber-soft">Sample requested</span>}
              {active.request_price && <span className="rounded-full bg-forest/40 px-3 py-1 font-mono text-[10px] uppercase text-emerald-300">Price requested</span>}
              {active.request_spec && <span className="rounded-full bg-offwhite/10 px-3 py-1 font-mono text-[10px] uppercase text-offwhite/60">Spec sheet requested</span>}
            </div>
            {active.message && <div><div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-offwhite/45">Message</div><p className="rounded-lg bg-offwhite/5 p-3 font-body text-sm text-offwhite/80">{active.message}</p></div>}
            {active.additional_requirements && <div><div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-offwhite/45">Additional Requirements</div><p className="rounded-lg bg-offwhite/5 p-3 font-body text-sm text-offwhite/80">{active.additional_requirements}</p></div>}
            {active.attachment && <a href={active.attachment} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-2 rounded-lg border border-offwhite/20 px-4 py-2 font-body text-sm text-offwhite/80 hover:bg-offwhite/10"><FileText className="h-4 w-4" /> View attachment</a>}
            <div className="grid grid-cols-1 gap-4 border-t border-offwhite/10 pt-4 sm:grid-cols-2">
              <Field label="Status"><select data-testid="rfq-status-select" className={inputCls} value={active.status} onChange={(e) => update(active.id, { status: e.target.value })}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
              <Field label="Assigned Admin"><input className={inputCls} value={active.assigned_admin || ""} onChange={(e) => setActive({ ...active, assigned_admin: e.target.value })} onBlur={(e) => update(active.id, { assigned_admin: e.target.value })} /></Field>
            </div>
            <Field label="Internal Notes"><textarea data-testid="rfq-notes" rows={3} className={inputCls} value={active.notes || ""} onChange={(e) => setActive({ ...active, notes: e.target.value })} onBlur={(e) => update(active.id, { notes: e.target.value })} /></Field>
            <div className="flex justify-between border-t border-offwhite/10 pt-4">
              <Btn variant="danger" onClick={() => setConfirm(active.id)}><Trash2 className="mr-1 inline h-3.5 w-3.5" /> Delete</Btn>
              <Btn variant="ghost" onClick={() => setActive(null)}>Close</Btn>
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={del} message="Delete this RFQ permanently?" />
    </div>
  );
}
